'use client'

import { cn } from '@/lib/utils/utils'
import { useMounted } from '@/lib/utils/utils-client'
import { useEffect, useState } from 'react'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { I18nValue, LangCode, useI18nValue } from '../i18n/lang-context'
import { Glossary } from '../jbda/glossary'
import MD from '../markdown'
import { Flex, FlexCol } from '../ui/flex'

export type Question = {
  question: I18nValue
  answer: I18nValue
}

export function Question({ question, index, background2, glossary, langCode }: { question: Question, index: number, background2?: boolean, glossary?: Glossary, langCode: LangCode }) {

  const i18nValue = useI18nValue()

  const [opened, setOpened] = useState<boolean>(true)

  const mounted = useMounted()
  useEffect(() => {
    if(!mounted || typeof window === 'undefined') return 
    // Access the anchor using window.location.hash
    const anchor = window.location.hash
    // If access to a direct FAQ line is requested, answer will be unfolded
    if (anchor === '#QUESTION_LINE_' + index || anchor === '#QUESTION_' + index ||
        anchor === '#ARROW_' + index || anchor === '#ANSWER_' + index)
      setOpened(true)
    else
      setOpened(false)
  }, [mounted])

  return (
    <FlexCol className='w-full transition-all' key={index}>
      <Flex id={'QUESTION_LINE_' + index}
            className={cn('w-full cursor-pointer flex-nowrap items-center justify-between gap-2 rounded-md p-2 px-3 text-lg transition-all sm:gap-4 sm:p-3 sm:px-4 hover:opacity-100',
                          opened ? (background2 ? 'bg-background' : 'bg-background2') : 'opacity-80',
                          background2 ? 'hover:bg-background' : 'hover:bg-background2')}
            onClick={() => setOpened(!opened)}>
        <div id={'QUESTION_' + index} className='select-none text-[15px] font-medium sm:text-lg'><MD langCode={langCode}>{i18nValue(question.question, langCode)}</MD></div>
        <div id={'ARROW_' + index} className='text-lg font-semibold opacity-70'>
          <MdKeyboardArrowDown size={24} className={cn('transition-all', opened ? 'rotate-180' : '')} />
        </div>
      </Flex>
      <div id={'ANSWER_' + index}
           className={cn('my-2 w-full rounded-lg p-2 px-3 text-[14px] transition-all duration-300 sm:my-3 sm:p-3 sm:px-4 sm:text-base',
                         opened ? '' : 'z-0 my-0 h-0 py-0 opacity-0 sm:my-0 sm:py-0',
                         background2 ? 'bg-background' : 'bg-background2')}>
        <Answer input={i18nValue(question.answer, langCode)}
                className={cn(opened ? '' : 'hidden')}
                glossary={glossary}
                langCode={langCode} />
      </div>
    </FlexCol>
  )
}

function Answer({ input, className = '', glossary, langCode }: { input: String, className?: string, glossary?: Glossary, langCode: LangCode }) {
  const paragraphs = input.split('\n')
  return <div>
    {paragraphs.map((p, i) => (
      <div key={i} className={className}>
        <MD glossary={glossary} langCode={langCode}>{p}</MD>
        {i < paragraphs.length - 1 && <br />}
      </div>
    )
    )}
  </div>
}
